import type { ChannelThreadId } from "@redotech/effect-api-shared/ids/definitions";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";

export interface RenderableThread {
  thread: ChannelThread;
  messageThreadIds: readonly ChannelThreadId[];
}

function isRedoChatThread(thread: ChannelThread): boolean {
  return (
    thread.integrationType === "redoChatAi" ||
    thread.integrationType === "redoChatFlow" ||
    thread.integrationType === "redoChatLive"
  );
}

function getThreadRenderGroupKey(thread: ChannelThread): string {
  if (isRedoChatThread(thread)) {
    // Some existing Redo Chat data has one physical thread per message because
    // the old lane key was not stable. Render those rows as one chat block while
    // keeping every underlying thread id available for message lookup.
    return `redoChat:${thread.externalThreadId}`;
  }
  return `thread:${thread.id}`;
}

export function groupThreadsForRendering(
  threads: readonly ChannelThread[],
): RenderableThread[] {
  const groupedThreads = new Map<string, RenderableThread>();

  for (const thread of threads) {
    const groupKey = getThreadRenderGroupKey(thread);
    const existing = groupedThreads.get(groupKey);

    if (existing) {
      groupedThreads.set(groupKey, {
        ...existing,
        messageThreadIds: [...existing.messageThreadIds, thread.id],
      });
      continue;
    }

    groupedThreads.set(groupKey, { thread, messageThreadIds: [thread.id] });
  }

  return [...groupedThreads.values()];
}
